import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';

import { APP_PERMISSION } from '@core/enum/permisson';
import { LeadSortFieldEnum, ReportTabEnum } from '@core/enum/report';
import { GetPerformanceParams, LeadReport, PerformanceParams } from '@core/models/interfaces/report';
import { ReportService } from '@core/services/report.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CustomPaginatorComponent } from '@shared/components/custom-paginator/custom-paginator.component';
import { EmptyTableComponent } from '@shared/components/empty-table/empty-table.component';
import { SortTableHeaderComponent } from '@shared/components/sort-table-header/sort-table-header.component';
import { downloadFile } from '@shared/utils/dowloadFile.util';
import { NgxPermissionsModule } from 'ngx-permissions';
import { LeadReportColumn, LeadExportFieldEnum, leadReportColumns } from '@core/enum/report';
import { LEAD_EXPORT_TO_SORT_FIELD } from '@core/constants/report';
import { Router, ActivatedRoute } from '@angular/router';
import { pickNotEmpty, serializeParams, DISPLAY_NAMES_URL_DELIMITER, extractFullName } from '@shared/utils/dataObject.util';

@Component({
  selector: 'app-lead-report',
  standalone: true,
  imports: [
    TableModule,
    TranslateModule,
    MenuModule,
    CommonModule,
    NgxPermissionsModule,
    SortTableHeaderComponent,
    CustomPaginatorComponent,
    EmptyTableComponent,
    ButtonComponent
  ],
  templateUrl: './lead-report.component.html',
  styleUrl: './lead-report.component.scss'
})
export class LeadReportComponent implements OnChanges {
  @Input() params: PerformanceParams;
  @Input() isRunning: boolean = false;

  @Output() isRunningChange = new EventEmitter<boolean>();

  readonly Permission = APP_PERMISSION;
  readonly LeadSortFieldEnum = LeadSortFieldEnum;
  readonly LeadExportFieldEnum = LeadExportFieldEnum;
  readonly LEAD_EXPORT_TO_SORT_FIELD = LEAD_EXPORT_TO_SORT_FIELD;
  readonly translatePrefix = 'report.';
  readonly messagePrefix = 'report.message.';
  readonly exportOptions: MenuItem[] = [
    {
      title: 'CSV',
      img: '/assets/images/icons/file-csv.svg',
      command: () => {
        this.onExportReport('CSV');
      }
    },
    {
      title: 'PDF',
      img: '/assets/images/icons/file-pdf.svg',
      command: () => {
        this.onExportReport('PDF');
      }
    }
  ];

  leadReportDate: Date;
  isFetched: boolean = false;
  isLoading: boolean = false;
  leadReport: LeadReport[] = [];
  totalReports: number = 0;
  pagination = {
    page: 0,
    size: 10
  };
  options: number[] = [5, 10, 20, 100];
  sortField: string = '';
  sortOrder: number = 1;
  isExporting: boolean = false;

  constructor(
    private reportService: ReportService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['params']?.currentValue) {
      this.leadReportDate = new Date();
      this.pagination = {
        page: 0,
        size: 10
      };
      this.fetchLeadReport();
    }
  }

  get leadReportParams(): GetPerformanceParams {
    const groupIds = this.params.groups?.map(item => item.key)?.filter(item => !!item) as string[];
    return {
      ...this.params,
      groupIds
    };
  }

  fetchLeadReport() {
    const sorts = this.sortField ? (this.sortOrder < 0 ? this.sortField : `-${this.sortField}`) : '';
    const params = this.leadReportParams;

    this.isLoading = true;
    this.reportService
      .getLeadReport({
        ...params,
        page: this.pagination.page,
        size: this.pagination.size,
        sorts
      })
      .subscribe({
        next: res => {
          this.isLoading = false;
          this.isFetched = true;
          this.leadReport = res.data.content;
          this.totalReports = res.data.total;

          this.isRunning = false;
          this.isRunningChange.emit(this.isRunning);
          this.onUpdatedURL()
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  onSort(event: { field: string; order: number }) {
    this.sortField = event.field;
    this.sortOrder = event.order;

    this.fetchLeadReport();
  }

  onPagination() {
    this.fetchLeadReport();
  }

  onExportReport(type: 'CSV' | 'PDF') {
    this.isExporting = true;
    this.reportService.exportLeadReport(type, { ...this.params }).subscribe({
      next: res => {
        downloadFile(res, 'Lead Report');
        this.isExporting = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          detail: this.translateService.instant(this.messagePrefix + 'export-lead-report-failed')
        });
      }
    });
  }

  get visibleColumns(): LeadReportColumn[] {
    return leadReportColumns.filter(col => {
      const index = this.params.visibleFields?.findIndex(e => e === LeadExportFieldEnum[col.key])
      return index !== -1
    })
  }

  get isUserColumnIncluded() {
    return this.visibleColumns.filter(e => e.key === LeadExportFieldEnum.USER).length > 0;
  }

  onUpdatedURL() {
    this.params = {
      ...this.params,
      type: ReportTabEnum.LEAD
    };
    const params = pickNotEmpty(this.params);
    const serializedParams = serializeParams(params);

    // Dùng URLSearchParams để build query string, tránh Angular Router iterate string.
    const urlParams = new URLSearchParams();
    for (const [key, value] of Object.entries(serializedParams)) {
      if (value != null) {
        urlParams.set(key, String(value));
      }
    }

    if (this.params?.users?.length) {
      const names = this.params.users
        .map(u => extractFullName(u))
        .filter(n => n.length > 0);
      if (names.length > 0) {
        urlParams.set('userNames', names.join(DISPLAY_NAMES_URL_DELIMITER));
      }
    }

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    history.replaceState({}, '', newUrl);
  }
}
